/* eslint-disable dot-notation */

import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { RouterModule, Routes, NavigationEnd, Router } from '@angular/router';
import { MenuItemComponent } from '../menu-item/menu-item.component';
import { AuthState } from '../../../core/authentication/auth.state';
import { Store } from '@ngxs/store';
import { appRoutes } from '../../../app.routes';
import { EditorRole } from '../../../editor/editor.role.enum';
import { BehaviorSubject, filter } from 'rxjs';
import { EditorEntity } from '../../../editor/editor.entity';

export interface MenuItem {
  icon?: string
  label?: string
  route?: string
  children?: MenuItem[]
  separator?: boolean
  endIcon?: string
  visible: boolean
}

@Component({
  selector: 'pixl-sidenav-menu',
  imports: [
    MatListModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    MenuItemComponent
  ],
  templateUrl: './sidenav-menu.component.html',
  styleUrl: './sidenav-menu.component.scss'
})
export class SidenavComponent implements OnInit {
  private store = inject(Store);
  private editor: EditorEntity | null = null;
  private router = inject(Router);

  public menuItems: MenuItem[] = [];
  public sideNavCollapsed = signal(false);
  public openedMenus$ = new BehaviorSubject<string[]>([]);

  ngOnInit() {
    this.editor = this.store.selectSnapshot(AuthState.getEditor);
    // Das Menü wird aus den appRoutes gebaut
    this.menuItems = this.buildNavItems(appRoutes);

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkInitialMenuState();
    });

    this.checkInitialMenuState();
  }

  public toggleMenu(route: string): void {
    const currentOpened = this.openedMenus$.getValue();
    if (currentOpened.includes(route)) {
      this.openedMenus$.next(currentOpened.filter(r => r !== route));
    }
    else {
      this.openedMenus$.next([...currentOpened, route]);
    }
  }

  private checkInitialMenuState(): void {
    const fullPath = this.router.url.split('?')[0];
    const newOpenMenus: string[] = [];
    const currentOpened = this.openedMenus$.getValue();

    const findOpenMenus = (items: MenuItem[], currentPath: string) => {
      for (const item of items) {
        // Trennlinien-Einträge überspringen, da sie nicht navigieren
        if (item.separator) {
          continue;
        }

        if (item.children && item.route && currentPath.startsWith(item.route)) {
          if (!newOpenMenus.includes(item.route)) {
            newOpenMenus.push(item.route);
          }
          findOpenMenus(item.children, currentPath);
        }
      }
    };

    findOpenMenus(this.menuItems, fullPath);

    const combinedMenus = new Set([...currentOpened, ...newOpenMenus]);
    this.openedMenus$.next(Array.from(combinedMenus));
  }

  private buildNavItems(routes: Routes, parentPath = ''): MenuItem[] {
    const navItems: MenuItem[] = [];
    routes.forEach(route => {
      if (!route.data) {
        return;
      }

      if (route.data['isSeparator']) {
        let isVisible = !!route.data['visible'];
        if (isVisible && route.data['roles']) {
          const roles = route.data['roles'] as EditorRole[];
          isVisible = this.checkIfVisible(roles);
        }
        if (isVisible) {
          navItems.push({ separator: true, visible: true });
        }
        return;
      }

      if (route.redirectTo || route.path === '**') {
        return;
      }

      if (route.data['visible']) {
        const fullPath = parentPath ? `${parentPath}/${route.path}` : route.path;
        let isVisible = true;
        if (route.data['roles']) {
          const roles = route.data['roles'] as EditorRole[];
          isVisible = this.checkIfVisible(roles);
        }

        const navItem: MenuItem = {
          route: fullPath?.startsWith('/') ? fullPath : `/${fullPath}`,
          label: route.data['label'],
          icon: route.data['icon'],
          visible: isVisible
        };

        if (route.children && route.children.length > 0) {
          const childNavItems = this.buildNavItems(route.children, fullPath);
          if (childNavItems.length > 0) {
            navItem.children = childNavItems;
          }
        }
        navItems.push(navItem);
      }
    });
    return navItems;
  }

  private checkIfVisible(roles: EditorRole[]): boolean {
    if (!this.editor) {
      return false;
    }
    return roles.includes(this.editor.role as EditorRole);
  }
}
